import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from '@material-ui/core';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import PageTitle from '../../ui/PageTitle';
import { useButtonsStyles } from '../../../styles/useButtonsStyles';
import { useListingsFiltrationStyles } from '../../../styles/useListingsFiltrationStyles';
import { useYardiSyncAuditEventsQuery, useYardiSyncPropertiesQuery } from '../../../queries/yardiSync';
import { YardiSyncAuditEvent } from '../../../types/yardiSync';
import { formatDateTimeToLocal } from '../../../formatters/dateFormatters/dateFormatters';
import { PRINT_HYPHEN } from '../../../constants';

const AUDIT_EVENTS_LIMIT = 200;
const ENTITY_TYPES = ['building', 'floor', 'space', 'lease', 'document'];
const ACTION_TYPES = ['create', 'update', 'delete', 'soft_delete'];

const getErrorMessage = (error: unknown, fallback: string): string => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof responseData === 'string') {
    return responseData;
  }
  if (responseData && typeof responseData === 'object') {
    const data = responseData as { error?: string; message?: string };
    if (data.error) {
      return data.error;
    }
    if (data.message) {
      return data.message;
    }
  }
  const message = (error as Error)?.message;
  return message || fallback;
};

const parsePayload = (payload: string | null | undefined) => {
  if (!payload) {
    return {
      isJson: false,
      content: '',
    };
  }
  try {
    const parsed = JSON.parse(payload);
    return {
      isJson: true,
      content: JSON.stringify(parsed, null, 2),
    };
  } catch (error) {
    return {
      isJson: false,
      content: payload,
    };
  }
};

const YardiSyncAuditEvents: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const runId = Number(id);
  const history = useHistory();
  const location = useLocation();
  const buttonClasses = useButtonsStyles();
  const filterClasses = useListingsFiltrationStyles();
  const query = new URLSearchParams(location.search);

  const [propertyCode, setPropertyCode] = useState<string>(query.get('propertyCode') || '');
  const [buildingCode, setBuildingCode] = useState<string>(query.get('buildingCode') || '');
  const [entityType, setEntityType] = useState<string>(query.get('entityType') || '');
  const [action, setAction] = useState<string>(query.get('action') || '');
  const [offset, setOffset] = useState(0);
  const [eventsList, setEventsList] = useState<YardiSyncAuditEvent[]>([]);

  const { data: propertiesData } = useYardiSyncPropertiesQuery();
  const {
    data: auditEventsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useYardiSyncAuditEventsQuery(
    runId,
    AUDIT_EVENTS_LIMIT,
    offset,
    propertyCode || undefined,
    buildingCode || undefined,
    entityType || undefined,
    action || undefined,
  );

  useEffect(() => {
    query.set('propertyCode', propertyCode);
    query.set('buildingCode', buildingCode);
    query.set('entityType', entityType);
    query.set('action', action);
    history.replace({
      pathname: location.pathname,
      search: `?${query.toString()}`,
    });
    setOffset(0);
    setEventsList([]);
  }, [propertyCode, buildingCode, entityType, action]);

  useEffect(() => {
    if (!auditEventsData) {
      return;
    }
    setEventsList((prev) => (offset === 0 ? auditEventsData : [...prev, ...auditEventsData]));
  }, [auditEventsData, offset]);

  const buildingCodeOptions = useMemo(() => {
    const codes = new Set<string>();
    eventsList.forEach((event) => {
      if (event.buildingCode) {
        codes.add(event.buildingCode);
      }
    });
    return Array.from(codes);
  }, [eventsList]);

  const columns = useMemo<MRT_ColumnDef<YardiSyncAuditEvent>[]>(() => [
    {
      header: 'Action',
      accessorKey: 'action',
      enableSorting: false,
    },
    {
      header: 'Entity Type',
      accessorKey: 'entityType',
      enableSorting: false,
    },
    {
      header: 'Entity ID',
      accessorKey: 'entityId',
      enableSorting: false,
      size: 100,
    },
    {
      header: 'Property',
      accessorKey: 'propertyCode',
      enableSorting: false,
    },
    {
      header: 'Building',
      accessorKey: 'buildingCode',
      enableSorting: false,
    },
    {
      header: 'Created At',
      accessorKey: 'createdAt',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (row.createdAt ? formatDateTimeToLocal(row.createdAt) : PRINT_HYPHEN),
      enableSorting: false,
      size: 160,
    },
  ], []);

  const hasMore = auditEventsData ? auditEventsData.length === AUDIT_EVENTS_LIMIT : false;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}>
        <PageTitle>{`Run ${runId} Audit Events`}</PageTitle>
      </Box>
      <Box display="flex" alignItems="center" mb={2} className={filterClasses.listingFilterWrapper} flexWrap="wrap">
        <Box mr={2} minWidth={180}>
          <FormControl fullWidth>
            <InputLabel shrink={!!propertyCode} htmlFor="filter-property-code">Property Code</InputLabel>
            <Select
              native
              value={propertyCode}
              onChange={(event) => setPropertyCode(String(event.target.value))}
              label="Property Code"
              inputProps={{
                name: 'filter-property-code',
                id: 'filter-property-code',
              }}
            >
              <option aria-label="None" value="" />
              {(propertiesData || []).map((property) => (
                <option key={property.id} value={property.propertyCode}>
                  {property.propertyCode}
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mr={2} minWidth={180}>
          <FormControl fullWidth>
            <InputLabel shrink={!!buildingCode} htmlFor="filter-building-code">Building Code</InputLabel>
            <Select
              native
              value={buildingCode}
              onChange={(event) => setBuildingCode(String(event.target.value))}
              label="Building Code"
              inputProps={{
                name: 'filter-building-code',
                id: 'filter-building-code',
              }}
            >
              <option aria-label="None" value="" />
              {buildingCodeOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mr={2} minWidth={160}>
          <FormControl fullWidth>
            <InputLabel shrink={!!entityType} htmlFor="filter-entity-type">Entity Type</InputLabel>
            <Select
              native
              value={entityType}
              onChange={(event) => setEntityType(String(event.target.value))}
              label="Entity Type"
              inputProps={{
                name: 'filter-entity-type',
                id: 'filter-entity-type',
              }}
            >
              <option aria-label="None" value="" />
              {ENTITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mr={2} minWidth={160}>
          <FormControl fullWidth>
            <InputLabel shrink={!!action} htmlFor="filter-action">Action</InputLabel>
            <Select
              native
              value={action}
              onChange={(event) => setAction(String(event.target.value))}
              label="Action"
              inputProps={{
                name: 'filter-action',
                id: 'filter-action',
              }}
            >
              <option aria-label="None" value="" />
              {ACTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          className={buttonClasses.standard}
          variant="outlined"
          onClick={() => history.push(`/yardi/runs/${runId}`)}
        >
          Back to Run
        </Button>
      </Box>
      {isError ? (
        <Typography color="error">
          {getErrorMessage(error, 'Unable to load audit events.')}
        </Typography>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={eventsList}
          enableTopToolbar={false}
          enableColumnResizing
          enableColumnActions={false}
          enableFullScreenToggle={false}
          enableRowOrdering={false}
          enableSorting={false}
          enableGlobalFilterModes={false}
          enableDensityToggle={false}
          state={{ isLoading: isLoading || isFetching }}
          layoutMode="grid"
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
              display: 'table',
            },
          }}
          renderDetailPanel={({ row }) => {
            const before = parsePayload(row.original.beforePayload);
            const after = parsePayload(row.original.afterPayload);
            return (
              <Box padding={2}>
                <Typography variant="subtitle2">Before</Typography>
                <Box
                  component="pre"
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {before.content || PRINT_HYPHEN}
                </Box>
                <Box mt={2}>
                  <Typography variant="subtitle2">After</Typography>
                  <Box
                    component="pre"
                    style={{
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {after.content || PRINT_HYPHEN}
                  </Box>
                </Box>
              </Box>
            );
          }}
        />
      )}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            className={buttonClasses.standard}
            variant="outlined"
            color="primary"
            disabled={isFetching}
            onClick={() => setOffset((prev) => prev + AUDIT_EVENTS_LIMIT)}
          >
            Load more
          </Button>
        </Box>
      )}
    </>
  );
};

export default YardiSyncAuditEvents;
